/**
 * KR mini-calendar for one location: a month grid showing which availability
 * windows are FREE vs BOOKED (and past). Click a day to see its windows.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Loader2, CalendarDays, Clock, User,
} from 'lucide-react';
import { retailProgramAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { to12h } from '@/components/common/TimeSelect';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const pad = (n) => String(n).padStart(2, '0');
const keyOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

export default function LocationCalendarDialog({ open, onOpenChange, locationId, locationName }) {
  const [loading, setLoading] = useState(true);
  const [windows, setWindows] = useState([]);
  const [cursor, setCursor] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (!open || !locationId) return;
    setLoading(true);
    setSelectedDay(null);
    retailProgramAPI.getLocation(locationId)
      .then((r) => {
        const all = r.data.all_windows || [];
        setWindows(all);
        // Jump to the month of the first upcoming (non-past) window.
        const upcoming = all.find((w) => !w.past) || all[0];
        if (upcoming?.date) {
          const [yy, mm] = upcoming.date.split('-').map((x) => parseInt(x, 10));
          setCursor({ y: yy, m: mm - 1 });
        }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load calendar'))
      .finally(() => setLoading(false));
  }, [open, locationId]);

  const byDate = useMemo(() => {
    const map = {};
    windows.forEach((w) => { (map[w.date] ||= []).push(w); });
    return map;
  }, [windows]);

  const grid = useMemo(() => {
    const firstDow = new Date(cursor.y, cursor.m, 1).getDay();
    const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) cells.push(null);
    for (let d = 1; d <= days; d += 1) cells.push(d);
    return cells;
  }, [cursor]);

  const shift = (delta) => {
    setSelectedDay(null);
    setCursor((c) => {
      const nm = c.m + delta;
      return { y: c.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });
  };

  const selWindows = selectedDay ? (byDate[selectedDay] || []) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="location-calendar-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <CalendarDays size={18} className="text-emerald-600" /> {locationName || 'Location'} — Availability
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-16 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
        ) : (
          <div className="space-y-3">
            {/* Month nav + legend */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="loc-cal-prev"><ChevronLeft size={15} /></Button>
                <span className="text-sm font-semibold text-slate-800 w-36 text-center">{MONTHS[cursor.m]} {cursor.y}</span>
                <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="loc-cal-next"><ChevronRight size={15} /></Button>
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Free</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" /> Booked</span>
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-7 gap-1 text-center">
              {DOW.map((d) => <div key={d} className="text-[10px] uppercase tracking-wider text-slate-400 py-1">{d}</div>)}
              {grid.map((d, i) => {
                if (!d) return <div key={`b-${i}`} />;
                const k = keyOf(cursor.y, cursor.m, d);
                const ws = byDate[k] || [];
                const free = ws.filter((w) => !w.booked && !w.past).length;
                const booked = ws.filter((w) => w.booked).length;
                const pastOnly = ws.length > 0 && free === 0 && booked === 0;
                const has = ws.length > 0;
                const isSel = selectedDay === k;
                return (
                  <button key={k} type="button"
                    onClick={() => has && setSelectedDay(isSel ? null : k)}
                    data-testid={`loc-cal-day-${k}`}
                    disabled={!has}
                    className={`aspect-square rounded-lg border p-1 flex flex-col items-center justify-start text-[11px] transition-colors ${isSel ? 'border-emerald-500 bg-emerald-50' : has ? 'border-slate-200 hover:border-emerald-300 bg-white' : 'border-transparent text-slate-300'}`}>
                    <span className={has ? 'font-medium text-slate-700' : ''}>{d}</span>
                    {has && (
                      <span className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
                        {free > 0 && <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-semibold">{free}</span>}
                        {booked > 0 && <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-rose-100 text-rose-700 text-[9px] font-semibold">{booked}</span>}
                        {pastOnly && <span className="inline-flex items-center justify-center h-4 px-1 rounded-full bg-slate-100 text-slate-400 text-[9px] font-medium">past</span>}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected-day detail */}
            {selectedDay && (
              <div className="rounded-lg border border-slate-200 p-3 space-y-1.5" data-testid="loc-cal-day-detail">
                <p className="text-xs font-semibold text-slate-600">
                  {new Date(`${selectedDay}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
                {selWindows.map((w) => (
                  <div key={w.window_id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-1.5 text-slate-700">
                      <Clock size={12} className="text-slate-400" />{to12h(w.start)}–{to12h(w.end)}
                    </span>
                    {w.booked ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                        <User size={10} /> Booked by {w.booked_by?.agent_name || 'agent'}
                      </span>
                    ) : w.past ? (
                      <span className="text-[11px] text-slate-400 italic">Past</span>
                    ) : (
                      <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Free</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {windows.length === 0 && (
              <p className="text-center text-xs text-slate-400 italic py-4">No availability windows defined yet.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
